import { useEffect, useState, type FormEvent } from 'react';

import { Alert, Button, Card, FormField, Input, Modal } from '@playerbase/ui';
import { SendInviteFormSchema } from '@playerbase/types';

import { supabase } from '../../lib/supabase.js';

type Props = {
  studioId: string;
  studioName: string;
  onClose: () => void;
  onSent: () => void;
};

function errorCopy(code: string, studioName: string): string {
  switch (code) {
    case 'email_already_member':
      return 'That email is already a member of ' + studioName + '.';
    case 'pending_invite_exists':
      return 'There is already a pending invite for that email. Resend or revoke it from the list below.';
    case 'not_a_member':
      return 'You are not a member of this studio.';
    case 'email_send_failed':
      return 'We could not send the invite email. Please try again.';
    case 'studio_not_found':
      return 'This studio could not be found.';
    case 'invalid_body':
      return 'Please check the email address.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

export function InviteModal({ studioId, studioName, onClose, onSent }: Props): JSX.Element {
  const [email, setEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Close on Escape so the modal feels keyboard-native.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [onClose, submitting]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setEmailError(null);
    setServerError(null);

    const parsed = SendInviteFormSchema.safeParse({
      email,
      personalMessage: personalMessage.trim() === '' ? undefined : personalMessage,
    });
    if (!parsed.success) {
      const emailIssue = parsed.error.issues.find((i) => i.path[0] === 'email');
      if (emailIssue !== undefined) setEmailError(emailIssue.message);
      const otherIssue = parsed.error.issues.find((i) => i.path[0] !== 'email');
      if (otherIssue !== undefined) setServerError(otherIssue.message);
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke<{ error?: string }>(
        'studio-invites',
        {
          body: {
            action: 'send',
            studioId,
            email: parsed.data.email,
            personalMessage: parsed.data.personalMessage,
          },
        },
      );

      // supabase-js wraps non-2xx responses into FunctionsHttpError; we still
      // surface the typed error code from the JSON body when available.
      const code = data !== null && data !== undefined && typeof data === 'object' && 'error' in data
        ? (data as { error?: string }).error
        : undefined;
      if (error !== null || code !== undefined) {
        const message = errorCopy(code ?? 'unknown', studioName);
        if (code === 'email_already_member' || code === 'pending_invite_exists') {
          setEmailError(message);
        } else {
          setServerError(message);
        }
        return;
      }

      onSent();
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      busy={submitting}
      labelledById="invite-modal-title"
    >
      <Card>
        <Card.Header>
          <Card.Title id="invite-modal-title" className="font-display text-xl font-bold">
            Invite a teammate
          </Card.Title>
          <Card.Description>
            They will receive an email with a link to accept.
          </Card.Description>
        </Card.Header>
        <Card.Content>
          <form onSubmit={(e) => { void handleSubmit(e); }} className="flex flex-col gap-4" noValidate>
            {serverError !== null && <Alert tone="danger">{serverError}</Alert>}

            <FormField error={emailError ?? undefined}>
              <FormField.Label>Email</FormField.Label>
              <FormField.Control>
                <Input
                  type="email"
                  autoComplete="off"
                  autoFocus
                  aria-label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full"
                />
              </FormField.Control>
              {emailError !== null && <FormField.ErrorText>{emailError}</FormField.ErrorText>}
            </FormField>

            <FormField>
              <FormField.Label>Personal message (optional)</FormField.Label>
              <FormField.Control>
                <textarea
                  aria-label="Personal message"
                  value={personalMessage}
                  onChange={(e) => setPersonalMessage(e.target.value)}
                  maxLength={500}
                  rows={3}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Hey, joining you on PlayerBase..."
                />
              </FormField.Control>
              <FormField.HelpText>
                Included in the invite email. Up to 500 characters.
              </FormField.HelpText>
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send invite'}
              </Button>
            </div>
          </form>
        </Card.Content>
      </Card>
    </Modal>
  );
}
